import 'fitify-ui-landing/src/theme/Fonts.css'
import 'react-loading-skeleton/dist/skeleton.css'

import { getAnalytics, setUserProperties } from 'firebase/analytics'
import { getApp, initializeApp } from 'firebase/app'
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions'
import { getPerformance } from 'firebase/performance'
import { colors as themeColors } from 'fitify-ui'
import { useAnalytics } from 'fitify-ui/src/hooks/useAnalytics'
import { logEvent } from 'fitify-ui/src/utils/analytics'
import { ELEMENT_IDS } from 'fitify-ui-landing/src/@types/constants'
import {
  FitifyThemeProvider,
  ThemeVariants,
} from 'fitify-ui-landing/src/theme/index'
import { textVariants } from 'fitify-ui-landing/src/theme/textVariants/textVariants'
import type { AppProps } from 'next/app'
import { appWithTranslation, useTranslation } from 'next-i18next'
import { DefaultSeo } from 'next-seo'
import { useEffect } from 'react'
import TagManager from 'react-gtm-module'
import { SkeletonTheme } from 'react-loading-skeleton'

import { GlobalStyle } from '@/theme/globalStyle'
import { getLocalizedImagePathConverter } from '@/utils/getLocalizedImagePathConverter'

declare global {
  interface Window {
    // Used in VWO
    updateOptimizeConfig: (optimizeConfig: Record<string, unknown>) => void
  }
}

const app = initializeApp({
  apiKey: process.env.NEXT_PUBLIC_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_AUTH_DOMAIN,
  databaseURL: process.env.NEXT_PUBLIC_DATABASE_URL,
  projectId: process.env.NEXT_PUBLIC_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_MEASUREMENT_ID,
})

if (process.env.NEXT_PUBLIC_FUNCTIONS_EMULATOR_PORT) {
  connectFunctionsEmulator(
    getFunctions(getApp()),
    'localhost',
    parseInt(process.env.NEXT_PUBLIC_FUNCTIONS_EMULATOR_PORT)
  )
}

function MyApp({ Component, pageProps }: AppProps) {
  const { t, i18n } = useTranslation()
  useAnalytics(app)

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      TagManager.initialize({
        gtmId: process.env.NEXT_PUBLIC_GTM_ID,
      })

      getPerformance(app)

      const analytics = getAnalytics()
      const url = new URL(window.location.href)
      const utmSource = url.searchParams.get('utm_source')
      const utmMedium = url.searchParams.get('utm_medium')
      const utmCampaign = url.searchParams.get('utm_campaign')
      const utmContent = url.searchParams.get('utm_content')
      const utmTerm = url.searchParams.get('utm_term')

      setUserProperties(analytics, {
        source: utmSource,
        medium: utmMedium,
        campaign: utmCampaign,
      })

      if (utmSource && utmMedium && utmCampaign) {
        logEvent('utm_conversion', {
          source: utmSource,
          medium: utmMedium,
          campaign: utmCampaign,
          content: utmContent,
          term: utmTerm,
        })
      }
    }
  }, [])

  const theme = {
    variant: ThemeVariants.DIGITAL,
    textVariants: textVariants,
    colors: themeColors,
  }
  const { src: ogImage } = getLocalizedImagePathConverter(
    { imageSrc: '/assets/images/og/image_{{language}}.webp' },
    i18n.language
  )

  return (
    <FitifyThemeProvider theme={theme}>
      <GlobalStyle />
      <DefaultSeo
        openGraph={{
          images: [
            {
              url: ogImage,
            },
          ],
        }}
        twitter={{ cardType: 'summary_large_image' }}
        title={t('cl_landing_page_title')}
        description={t('cl_landing_page_subtitle')}
      />
      <SkeletonTheme
        baseColor={'#EEEEEE'}
        highlightColor={'#DFDFDF'}
        borderRadius="0.250rem"
        duration={1}
        enableAnimation
      >
        <div id={ELEMENT_IDS.home}>
          <Component {...pageProps} />
        </div>
      </SkeletonTheme>
    </FitifyThemeProvider>
  )
}

export default appWithTranslation(MyApp)
